import { useCallback, useMemo, useState } from 'react';

/**
 * A column that can be filtered on. `filterValue` returns the plain text that
 * IS the filterable value — deliberately separate from how the cell renders, so a
 * Status cell may draw a coloured badge while filtering on the word "Pending".
 *
 * That string is matched EXACTLY (not as a substring): the filter offers the
 * distinct values actually present in the column and the user picks from them, so
 * choosing "IT" can never also match "Quality, Safety & Compliance".
 */
export interface FilterableColumn<T> {
  columnId: string;
  header: string;
  filterValue: (row: T) => string;
  /**
   * Values hidden until the user asks for them. Rows carrying one of these are
   * left out while the column has NO explicit selection, but the value still
   * appears in the column's dropdown — so it reads as "there is more here,
   * choose it to see it" rather than as missing data.
   *
   * PTO uses this for Complete: finished bookings are noise during the weekly
   * review, but they are still one click away.
   */
  hiddenByDefault?: readonly string[];
}

export interface ColumnFilters<T> {
  /** Selected values per column id. An absent/empty list means "no filter". */
  selections: Readonly<Record<string, readonly string[]>>;
  /**
   * The values selectable in each column, in display order. Computed from the
   * rows surviving every OTHER column's filter (Excel semantics), so the choices
   * on offer can never produce an empty result.
   */
  options: Readonly<Record<string, readonly string[]>>;
  /** Replace one column's selection ( [] clears it ). */
  setSelection: (columnId: string, values: readonly string[]) => void;
  /** Clear every column filter. */
  clearAll: () => void;
  /** Rows surviving every active filter. */
  filtered: readonly T[];
  /** How many columns currently filter. */
  activeCount: number;
}

/**
 * Order filter choices the way a human scans them: numerically when every value
 * is a number ("8" before "10", which a plain string sort gets backwards), and
 * otherwise by locale. ISO dates sort correctly either way.
 */
function compareValues(a: string, b: string): number {
  const na = Number(a);
  const nb = Number(b);
  if (a.trim() !== '' && b.trim() !== '' && Number.isFinite(na) && Number.isFinite(nb)) {
    return na - nb;
  }
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Per-column, Excel-style filtering for a report table.
 *
 * Each column offers a dropdown of the distinct values currently in it; the user
 * ticks the ones to keep. Selections are AND-ed ACROSS columns and OR-ed WITHIN a
 * column — "Department is IT or Warehouse, and Status is Pending".
 *
 * A column may also declare {@link FilterableColumn.hiddenByDefault} values,
 * which are omitted until explicitly selected while still being offered in the
 * dropdown.
 *
 * Generic on purpose: every "My Reports" table is a long-lived, constantly-growing
 * list, so they all need this — a report supplies its columns and gets filtering
 * for free.
 */
export function useColumnFilters<T>(
  rows: readonly T[],
  columns: ReadonlyArray<FilterableColumn<T>>
): ColumnFilters<T> {
  const [selections, setSelections] = useState<Record<string, readonly string[]>>({});

  const setSelection = useCallback((columnId: string, values: readonly string[]) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (values.length === 0) {
        delete next[columnId];
      } else {
        next[columnId] = values;
      }
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setSelections({});
  }, []);

  /**
   * Does `row` satisfy every active filter except (optionally) one column's?
   * Excluding a column is what lets that column's own dropdown keep offering all
   * of its values while still reflecting the other filters.
   */
  const passes = useCallback(
    (row: T, exceptColumnId: string | null): boolean =>
      columns.every((col) => {
        if (col.columnId === exceptColumnId) {
          return true;
        }
        const selected = selections[col.columnId];
        if (selected === undefined || selected.length === 0) {
          // No explicit selection: everything shows EXCEPT the values this
          // column hides by default.
          return !(col.hiddenByDefault ?? []).includes(col.filterValue(row));
        }
        return selected.includes(col.filterValue(row));
      }),
    [columns, selections]
  );

  const filtered = useMemo(() => rows.filter((row) => passes(row, null)), [rows, passes]);

  const options = useMemo(() => {
    const result: Record<string, readonly string[]> = {};
    for (const col of columns) {
      const values = new Set<string>();
      for (const row of rows) {
        if (passes(row, col.columnId)) {
          values.add(col.filterValue(row));
        }
      }
      // A value the user has already picked stays listed even if the other
      // filters would now exclude it — otherwise their own selection could
      // vanish from the dropdown and become impossible to untick.
      for (const picked of selections[col.columnId] ?? []) {
        values.add(picked);
      }
      result[col.columnId] = [...values].sort(compareValues);
    }
    return result;
  }, [rows, columns, selections, passes]);

  return {
    selections,
    options,
    setSelection,
    clearAll,
    filtered,
    activeCount: Object.keys(selections).length,
  };
}
